import { useMemo, useState } from "react";
import { Users } from "lucide-react";

function PropertyTenantsCard({ p, tenants, roomNo }) {
  return (
    <div className="bg-white border border-slate-200" data-testid={`prop-tenants-${p.id}`}>
      <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Users className="h-4 w-4 text-slate-500 shrink-0" />
          <div className="font-display font-bold text-lg truncate">{p.name}</div>
          <span className="text-xs text-slate-500">· {p.address}</span>
        </div>
        <div className="text-xs text-slate-500">{tenants.length} active tenant{tenants.length === 1 ? "" : "s"}</div>
      </div>
      {tenants.length === 0 ? (
        <div className="p-6 text-sm text-slate-500">No tenants in this property yet.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {tenants.map((t) => (
            <li key={t.id} className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`prop-tenant-${t.id}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{t.name}</div>
                <div className="text-xs text-slate-500">{t.phone}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-slate-500">
                <span className="bg-slate-100 px-2 py-0.5">Room {roomNo(t.room_id)}</span>
                <span className="font-mono">₹{(t.rent_amount || 0).toLocaleString("en-IN")}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function TenantsByProperty({ items, tenants, rooms }) {
  const [filterId, setFilterId] = useState("all");
  const roomMap = useMemo(() => Object.fromEntries(rooms.map((r) => [r.id, r.room_number])), [rooms]);
  const roomNo = (rid) => roomMap[rid] || "—";
  const grouped = useMemo(() => {
    const visible = filterId === "all" ? items : items.filter((p) => p.id === filterId);
    const rn = (rid) => roomMap[rid] || "—";
    return visible.map((p) => ({
      p,
      list: tenants.filter((t) => t.property_id === p.id && t.status === "active")
        .sort((a, b) => rn(a.room_id).localeCompare(rn(b.room_id), undefined, { numeric: true })),
    }));
  }, [items, tenants, roomMap, filterId]);

  return (
    <div className="space-y-4" data-testid="tenants-by-property-section">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Residents</div>
          <h2 className="font-display text-xl sm:text-2xl font-black tracking-tighter">Tenants by property</h2>
        </div>
        <div className="w-full sm:w-72">
          <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1">Filter property</label>
          <select data-testid="prop-filter" value={filterId} onChange={(e) => setFilterId(e.target.value)}
                  className="w-full border border-slate-300 px-4 py-2.5 bg-white">
            <option value="all">All properties</option>
            {items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </div>
      <div className="grid gap-4">
        {grouped.map(({ p, list }) => <PropertyTenantsCard key={p.id} p={p} tenants={list} roomNo={roomNo} />)}
      </div>
    </div>
  );
}
